import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Mail, RefreshCw, AlertTriangle, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";

// Journal des e-mails envoyés par le site — par défaut les non délivrés, pour relance.
export default function AdminEmailLogs() {
  const [items, setItems] = useState([]);
  const [totalFailed, setTotalFailed] = useState(0);
  const [status, setStatus] = useState("failed");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/email-logs?status=${status}`);
      setItems(data.items || []);
      setTotalFailed(data.total_failed || 0);
    } catch { toast.error("Erreur de chargement."); }
    finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const copy = async (txt) => { try { await navigator.clipboard.writeText(txt); toast.success("Copié !"); } catch { toast.info(txt); } };

  return (
    <div data-testid="admin-email-logs-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2" data-testid="admin-email-logs-title">
            <Mail className="text-[#E05638]" size={26} /> Journal des e-mails
          </h1>
          <p className="text-slate-500 text-xs mt-1">
            Les e-mails non délivrés (adresse invalide ou injoignable). Contactez ces personnes autrement, ou corrigez l'adresse dans leur demande/compte.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-email-logs-reload">
          <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </div>

      <div className="mt-4 inline-flex rounded-xl bg-white border border-slate-200 p-1 gap-1" data-testid="admin-email-logs-filter">
        <button onClick={() => setStatus("failed")} data-testid="admin-email-logs-filter-failed"
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1 ${status === "failed" ? "bg-[#E05638] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          <AlertTriangle size={14} /> Non délivrés{totalFailed ? ` (${totalFailed})` : ""}
        </button>
        <button onClick={() => setStatus("sent")} data-testid="admin-email-logs-filter-sent"
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1 ${status === "sent" ? "bg-[#E05638] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          <CheckCircle2 size={14} /> Envoyés
        </button>
      </div>

      {loading ? <div className="mt-6 text-slate-500 text-sm">Chargement…</div> :
       items.length === 0 ? (
         <div className="mt-6 bg-white border border-dashed rounded-2xl p-10 text-center text-slate-500 text-sm" data-testid="admin-email-logs-empty">
           {status === "failed" ? "Aucun e-mail non délivré 🎉" : "Aucun e-mail dans le journal."}
         </div>
       ) : (
         <div className="mt-5 space-y-2" data-testid="admin-email-logs-list">
           {items.map((it) => (
             <div key={it.id} className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-start gap-3" data-testid={`admin-email-log-${it.id}`}>
               <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold shrink-0 ${it.status === "sent" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-700"}`}>
                 {it.status === "sent" ? <><CheckCircle2 size={11} /> Envoyé</> : <><AlertTriangle size={11} /> Échec</>}
               </span>
               <div className="min-w-0 flex-1">
                 <div className="font-semibold text-slate-900 text-sm truncate">{it.to || "—"}</div>
                 <div className="text-xs text-slate-600 truncate">{it.subject || "(sans objet)"}</div>
                 {it.reason && <div className="text-[11px] text-rose-600 mt-0.5 break-words">{it.reason}</div>}
                 <div className="text-[11px] text-slate-400 mt-0.5">{it.created_at ? new Date(it.created_at).toLocaleString("fr-FR") : ""}</div>
               </div>
               {it.to && (
                 <div className="flex items-center gap-1.5 shrink-0">
                   <a href={`mailto:${it.to}`} className="text-xs px-2 py-1 rounded-lg border border-slate-200 hover:border-slate-400 inline-flex items-center gap-1" data-testid={`admin-email-log-mailto-${it.id}`}><Mail size={12} /> Écrire</a>
                   <button onClick={() => copy(it.to)} className="text-xs px-2 py-1 rounded-lg border border-slate-200 hover:border-slate-400 inline-flex items-center gap-1" data-testid={`admin-email-log-copy-${it.id}`}><Copy size={12} /> Copier</button>
                 </div>
               )}
             </div>
           ))}
         </div>
       )}
    </div>
  );
}
